import { flash, Modal, numberFormat, SubmitButton } from "@xola/ui-kit";
import {
    FormField,
    getPaymentLineItems,
    isGiftPurchaseItem,
    isOtherSellerPurchase,
    isStoreCreditPurchaseItem,
    requiredNumberMoreThanZero,
} from "common";
import React, { useState } from "react";
import { useMutation } from "react-query";
import {
    ExperiencePurchaseItem,
    LineItemObject,
    LineItemType,
    PaymentMethod,
    Purchase,
    PurchaseItem,
    SellerPaymentMethod,
    Traveler,
} from "types";
import { object } from "yup";
import { api } from "../../api/api";
import { useSeller } from "../../hooks/useAccount";
import { getAmountForTraveler, getPaymentLineItemsWithRefunds } from "../../models/Purchase";
import { updateCache } from "../../utils/updateCache";
import { XolaFormik } from "../Common/XolaFormik";
import { CurrencyInputField } from "../Field/CurrencyInputField";
import { PaymentMethodButtons } from "./PaymentMethodButtons";

interface SplitPayRemovePayerModalProps {
    purchase: Purchase;
    item: ExperiencePurchaseItem;
    isOpen: boolean;
    onClose: () => void;
    traveler?: Traveler;
}

interface AddLineItemOperationProps {
    path: string;
    op: string;
    data: {
        amount: number;
        name: string;
        object: LineItemObject;
        payload: LineItemType;
        payment: { method?: ID; card?: string };
        traveler: {
            id?: ID;
            name?: string;
        };
        refundToStoreCredit?: boolean;
    };
}

const getMaxAmountForTraveler = (purchase: Purchase, traveler?: Traveler) => {
    return traveler && getAmountForTraveler(purchase, traveler?.id) < 0
        ? Math.abs(getAmountForTraveler(purchase, traveler?.id))
        : 0;
};

interface FormValues {
    amount: number;
    paymentMethod: SellerPaymentMethod;
}

const getInitialValues = (purchase: Purchase, traveler?: Traveler): FormValues => {
    return {
        amount: getMaxAmountForTraveler(purchase, traveler),
        paymentMethod: getPaymentMethod(purchase, traveler),
    };
};

const getValidationSchema = (purchase: Purchase, traveler?: Traveler) => {
    return object().shape({
        amount: requiredNumberMoreThanZero().max(
            getMaxAmountForTraveler(purchase, traveler),
            "must be less than balance",
        ),
    });
};

export const SplitPayRemovePayerModal = ({
    purchase,
    isOpen,
    onClose,
    traveler,
    item,
}: SplitPayRemovePayerModalProps) => {
    const seller = useSeller();
    const excludedPaymentMethods = getExcludedPaymentMethods(purchase, item, traveler);
    const [paymentMethod, setPaymentMethod] = useState<SellerPaymentMethod>(getPaymentMethod(purchase, traveler));
    const handlePaymentMethodChange = (value: SellerPaymentMethod) => {
        setPaymentMethod(value);
    };

    const originalPaymentMethod = getPaymentMethod(purchase, traveler);

    const {
        mutateAsync: returnPayment,
        isLoading: isUpdating,
        isSuccess: isUpdatingSuccess,
    } = useMutation(async (payload: ReturnType<typeof getPayload>) => {
        const { data } = await api.patch<Purchase>(`/purchases/${purchase.id}`, payload);
        return data;
    });

    const handleSubmit = async (values: FormValues) => {
        const isStoreCredit = values.paymentMethod?.id === PaymentMethod.STORE_CREDIT;
        const updatedPurchase = await returnPayment(
            getPayload(
                values.amount,
                isStoreCredit ? originalPaymentMethod : values.paymentMethod,
                traveler,
                isStoreCredit,
            ),
        );
        flash.show({ text: `Refund of ${numberFormat(values.amount, seller.currency)} applied` });
        updateCache("purchases", updatedPurchase);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <XolaFormik
                initialValues={getInitialValues(purchase, traveler)}
                validationSchema={getValidationSchema(purchase, traveler)}
                onSubmit={async (values: FormValues) => handleSubmit(values)}
            >
                {({ handleSubmit, setFieldValue }) => (
                    <>
                        <Modal.Header>Remove From Booking</Modal.Header>
                        <Modal.Body>
                            <div>
                                <FormField name="amount" label="Amount">
                                    <CurrencyInputField isRequired />
                                </FormField>
                            </div>
                            <div>
                                <PaymentMethodButtons
                                    isRefund
                                    value={paymentMethod}
                                    excludePaymentMethods={excludedPaymentMethods}
                                    isOtherSellerPurchase={isOtherSellerPurchase(purchase, seller.id)}
                                    onChange={(value) => {
                                        setFieldValue("paymentMethod", value);
                                        handlePaymentMethodChange(value);
                                    }}
                                />
                            </div>
                        </Modal.Body>
                        <Modal.Footer className="flex justify-end">
                            <SubmitButton isSuccess={isUpdatingSuccess} isLoading={isUpdating} onClick={handleSubmit}>
                                Remove
                            </SubmitButton>
                        </Modal.Footer>
                    </>
                )}
            </XolaFormik>
        </Modal>
    );
};

const getExcludedPaymentMethods = (purchase: Purchase, item: PurchaseItem, traveler?: Traveler) => {
    const excludedPaymentMethods = [];

    if (isStoreCreditPurchaseItem(item) || isGiftPurchaseItem(item)) {
        excludedPaymentMethods.push(PaymentMethod.STORE_CREDIT);
    }

    const hasCreditCardPayment = getPaymentLineItemsWithRefunds(purchase).some((lineItem) => {
        return (
            lineItem.type === LineItemType.PAYMENT &&
            lineItem.payment?.method === PaymentMethod.CREDIT_CARD &&
            lineItem.traveler?.id === traveler?.id
        );
    });

    if (!hasCreditCardPayment) {
        excludedPaymentMethods.push(PaymentMethod.CREDIT_CARD);
    }

    return excludedPaymentMethods;
};

const getPayload = (
    amount: number,
    paymentMethod?: SellerPaymentMethod,
    traveler?: Traveler,
    refundToStoreCredit?: boolean,
) => {
    const payment: { method?: ID; card?: string } = { method: paymentMethod?.id };
    if (paymentMethod?.card) {
        payment.card = paymentMethod.card;
    }

    const ops: AddLineItemOperationProps[] = [
        {
            path: "/lineItems",
            op: "addLineItem",
            data: {
                amount,
                name: "Return payment",
                object: LineItemObject.REFUND,
                payload: LineItemType.REFUND,
                payment: payment,
                traveler: {
                    id: traveler?.id,
                    name: traveler?.name,
                },
                ...(refundToStoreCredit ? { refundToStoreCredit: true } : {}),
            },
        },
    ];

    return {
        ops,
    };
};

const getPaymentMethod = (purchase: Purchase, traveler?: Traveler): SellerPaymentMethod => {
    const lineItems = getPaymentLineItems(purchase);
    const lineItem = lineItems.find(
        (lineItem) => lineItem.traveler?.id === traveler?.id && lineItem.type === LineItemType.PAYMENT,
    );
    const method = lineItem?.payment?.method;
    const card = lineItem?.payment?.card;

    if (method) {
        return {
            id: method,
            name: method,
            comment: { title: "Comment", enabled: true, required: false },
            ...(card ? { card } : null),
        };
    }

    return {
        id: PaymentMethod.CASH,
        name: PaymentMethod.CASH,
        comment: { title: "Comment", enabled: true, required: false },
    };
};
